import { Form, Modal, Tag, notification, DatePicker, Select } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "store/slices/modalsSlice";
import {
  createSupportCourseGroup,
  getSupportCourseGroups,
} from "store/slices/groupsSlice";
import { getAvailableStudentSupportCourses } from "store/slices/supportCoursesSlice";
import dayjs from "dayjs";

const { Option } = Select;

const NewSupportCourseGroupModal = () => {
  const { t } = useTranslation();
  const [form] = Form?.useForm();
  const dispatch = useDispatch();

  const { isModalOpen, modalType, modalProps } = useSelector(
    (state) => state?.modals
  );
  const { loading } = useSelector((state) => state?.groups);
  const open = isModalOpen && modalType === "new_support_course_group";
  const { supportCourseId } = modalProps || {};
  const [selectedType, setSelectedType] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const { supportCourse, availableStudentSupportCourses } = useSelector(
    (state) => state?.supportCourses
  );

  // Charger les étudiants disponibles
  useEffect(() => {
    console?.log(
      "Fetching available students for support course:",
      supportCourseId,
      open
    );

    if (open && supportCourseId) {
      console?.log(
        "Fetching available students for support course:",
        supportCourseId
      );
      dispatch(
        getAvailableStudentSupportCourses({
          supportCourseId,
          groupId: null,
        })
      );
    }
  }, [dispatch, open, supportCourseId, selectedState, selectedType]);

  // Pré-remplir le formateur quand le modal s'ouvre
  useEffect(() => {
    if (open && supportCourse?.instructor?._id) {
      form?.setFieldsValue({
        formers: [supportCourse?.instructor?._id],
      });
    }
  }, [open, supportCourse, form]);

  // Liste dynamique des types de groupe à partir du type du supportCourse
  const groupTypes = (supportCourse?.type || [])?.map((type) =>
    type === "atSchool" ? "atSchool" : type
  );

  // Mapping pour l'affichage et l'envoi
  const getApiGroupType = (type) => (type === "atSchool" ? "onsite" : type);

  const getAvailableStates = (type) => {
    const states = {
      online: ["particulier", "miniGroupe"],
      onsite: ["particulier", "miniGroupe", "groupe"],
      atTeacher: ["particulier"],
      atStudent: ["particulier"],
      atSchool: ["particulier", "miniGroupe", "groupe"], // Pour l'affichage, mais on envoie "onsite"
    };
    // Pour la logique, on traite atSchool comme onsite
    if (type === "atSchool") return states["atSchool"];
    return states[type] || [];
  };

  const onSubmit = async (values) => {
    try {
      if (!values?.students?.length) {
        throw new Error(t("groups.crud.form.error.required.students"));
      }

      // Correction ici : transformer atSchool en onsite pour l'API
      let groupeType = values?.groupeType;
      if (groupeType === "atSchool") {
        groupeType = "onsite";
      }

      const formattedValues = {
        ...values,
        supportCourseId,
        groupeType,
        groupState: values?.groupState,
        startingDate: dayjs(values?.startingDate)?.toISOString(),
        students: values?.students?.map((studentSupportCourseId) => ({
          studentSupportCourseId,
        })),
      };
      console?.log("Submitting new support course group:", formattedValues);
      await dispatch(createSupportCourseGroup(formattedValues))?.unwrap();
      await dispatch(getSupportCourseGroups(supportCourseId))?.unwrap();

      notification?.success({
        message: t("groups.crud.form.success"),
        duration: 3,
      });

      onClose();
    } catch (error) {
      notification?.error({
        message: t("groups.crud.form.error.failed"),
        description: error?.message || t("common.error.generic"),
        duration: 4,
      });
    }
  };

  const onClose = () => {
    form?.resetFields();
    dispatch(closeModal());
  };

  const validateStudentsCount = (_, value) => {
    if (!value || !form?.getFieldValue("groupState")) return Promise?.resolve();

    const count = value?.length;
    const state = form?.getFieldValue("groupState");

    switch (state) {
      case "particulier":
        if (count !== 1) {
          return Promise?.reject(
            t("groups.crud.form.error.validation.students.single")
          );
        }
        break;
      case "miniGroupe":
        if (count < 2 || count > 3) {
          return Promise?.reject(
            t("groups.crud.form.error.validation.students.mini")
          );
        }
        break;
      case "group":
        if (count < 4) {
          return Promise?.reject(
            t("groups.crud.form.error.validation.students.group")
          );
        }
        break;
      default:
        return Promise?.resolve();
    }

    return Promise?.resolve();
  };

  return (
    <Modal
      title={t("groups.crud.new")}
      open={open}
      onCancel={onClose}
      onOk={() => form?.submit()}
      confirmLoading={loading}
      width={800}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <Form.Item
          name="groupeType"
          label={t("groups.crud.form.label.type")}
          rules={[
            {
              required: true,
              message: t("groups.crud.form.error.required.type"),
            },
          ]}
        >
          <Select
            placeholder={t("groups.crud.form.placeholder.type")}
            onChange={(value) => {
              setSelectedType(value);
              form?.setFieldsValue({ groupState: undefined });
            }}
          >
            {groupTypes?.map((type) => (
              <Option key={type} value={type}>
                {t(
                  `supportCourse.type.${
                    type === "atSchool" ? "atSchool" : type
                  }`
                )}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="groupState"
          label={t("groups.crud.form.label.state")}
          rules={[
            {
              required: true,
              message: t("groups.crud.form.error.required.state"),
            },
          ]}
          dependencies={["groupeType"]}
        >
          <Select
            placeholder={t("groups.crud.form.placeholder.state")}
            disabled={!selectedType}
            onChange={(value) => {
              setSelectedState(value);
            }}
          >
            {getAvailableStates(selectedType)?.map((state) => (
              <Option key={state} value={state}>
                {t(`groups.state.${state?.replace(/\s+/g, "")?.toLowerCase()}`)}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="students"
          label={t("groups.crud.form.label.students")}
          rules={[
            {
              required: true,
              message: t("groups.crud.form.error.required.students"),
            },
            { validator: validateStudentsCount },
          ]}
        >
          <Select
            mode="multiple"
            placeholder={t("groups.crud.form.placeholder.students")}
            optionLabelProp="label"
            optionFilterProp="label"
            style={{ width: "100%" }}
          >
            {availableStudentSupportCourses?.map((student) => {
              const fullName =
                student?.studentSupportCourseId?.studentId?.fullName;
              const learningType =
                student?.studentSupportCourseId?.learningType ||
                t("common.notSpecified");
              const learningState =
                student?.studentSupportCourseId?.learningState;
              const sessions =
                student?.studentSupportCourseId?.numberOfSessions || 0;

              const displayLabel = `${fullName} (${sessions} ${t(
                "common.sessions"
              )})`;

              return (
                <Option
                  key={student?.studentSupportCourseId?._id}
                  value={student?.studentSupportCourseId?._id}
                  label={displayLabel}
                >
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <span style={{ fontWeight: "bold" }}>{fullName}</span>
                    <span style={{ color: "#1890ff" }}>
                      {sessions} {t("common.sessions")}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      fontSize: "12px",
                      color: "rgba(0, 0, 0, 0.45)",
                    }}
                  >
                    <span style={{ marginRight: "8px" }}>
                      <Tag color="blue">
                        {t(`students.form.option.${learningType}`)}
                      </Tag>
                    </span>
                    <span>
                      <Tag color="green">
                        {learningState
                          ? t(
                              `supportCourse.student.learningState.${learningState}`
                            )
                          : t("common.notSpecified")}
                      </Tag>
                    </span>
                  </div>
                </Option>
              );
            })}
          </Select>
        </Form.Item>

        <Form.Item
          name="startingDate"
          label={t("groups.crud.form.label.startDate")}
          rules={[
            {
              required: true,
              message: t("groups.crud.form.error.required.startDate"),
            },
          ]}
        >
          <DatePicker
            style={{ width: "100%" }}
            placeholder={t("groups.crud.form.placeholder.startDate")}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default NewSupportCourseGroupModal;
